import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import { BrowserTheme } from '@utils/theme'

export type UseBrowserTheme = () => BrowserTheme

type GetBrowserTheme = (queryClient: QueryClient) => BrowserTheme

const getBrowserTheme: GetBrowserTheme = (queryClient) =>
  queryClient.getQueryData<BrowserTheme>(['theme', 'browser']) as BrowserTheme

const useBrowserTheme: UseBrowserTheme = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [browserTheme, setBrowserTheme] = useState<BrowserTheme>(() =>
    getBrowserTheme(queryClient),
  )

  const updateType = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === 'theme' &&
        args.query.queryKey[1] === 'browser'
      ) {
        setBrowserTheme(getBrowserTheme(queryClient))
      }
    },
    [queryClient],
  )

  useEffect(() => {
    setBrowserTheme(getBrowserTheme(queryClient))

    unsubscribeFnRef.current = queryCache.subscribe(updateType)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, queryClient, updateType])

  return browserTheme
}

export { getBrowserTheme }

export default useBrowserTheme
